import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import { colors, shadows } from '../styles/theme';

const TabContainer = ({ title, tabs = [], defaultTab = null, urlSync = true, hideChildHeaders = true }) => {
  const getInitialTab = () => {
    if (urlSync && typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash && tabs.find(t => t.id === hash)) return hash;
    }
    return defaultTab || (tabs.length > 0 ? tabs[0].id : null);
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const tabBarRef = useRef(null);
  const activeTabRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    if (urlSync && activeTab) {
      window.history.replaceState(null, '', `#${activeTab}`);
    }
  }, [activeTab, urlSync]);

  useEffect(() => {
    if (activeTabRef.current && tabBarRef.current) {
      const bar = tabBarRef.current;
      const tab = activeTabRef.current;
      const barRect = bar.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      if (tabRect.left < barRect.left || tabRect.right > barRect.right) {
        tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab]);

  const updateFades = () => {
    const el = tabBarRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateFades();
    const el = tabBarRef.current;
    if (el) el.addEventListener('scroll', updateFades);
    window.addEventListener('resize', updateFades);
    return () => {
      if (el) el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
    };
  }, [tabs]);

  const active = tabs.find(t => t.id === activeTab);

  return (
    <div>
      <Header title={title} />
      <div style={{ background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Tab Bar */}
        <div style={{
          background: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: shadows.sm,
          display: 'flex',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}>
          {showLeftFade && (
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '32px',
              background: `linear-gradient(to right, ${colors.surface} 30%, transparent)`,
              zIndex: 2, pointerEvents: 'none',
            }} />
          )}
          <div
            ref={tabBarRef}
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '2px',
              padding: '0 16px',
              flex: 1,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeTabRef : null}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? `3px solid ${colors.primary}` : '3px solid transparent',
                    padding: '13px 18px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: isActive ? '600' : '500',
                    color: isActive ? colors.primary : colors.textSecondary,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                    fontFamily: 'inherit',
                    letterSpacing: isActive ? '-0.01em' : '0',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = colors.textPrimary; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = colors.textSecondary; }}
                >
                  {tab.icon && <span style={{ fontSize: '15px' }}>{tab.icon}</span>}
                  {tab.label}
                </button>
              );
            })}
          </div>
          {showRightFade && (
            <div style={{
              position: 'absolute', right: 0, top: 0, bottom: 0, width: '32px',
              background: `linear-gradient(to left, ${colors.surface} 30%, transparent)`,
              zIndex: 2, pointerEvents: 'none',
            }} />
          )}
        </div>

        <style>{`
          div[style*="scrollbarWidth"]::-webkit-scrollbar { display: none; }
        `}</style>

        {/* Tab Content */}
        <div style={{ padding: '20px' }} className={hideChildHeaders ? 'tab-hide-child-header' : ''}>
          {active && active.component}
        </div>
        {hideChildHeaders && (
          <style>{`.tab-hide-child-header [data-page-header="true"] { display: none !important; }`}</style>
        )}
      </div>
    </div>
  );
};

export default TabContainer;
